import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useFetchUserData } from "app/hook";
import { actions as configActions } from "app/configSlice";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { IconArrowLeft, IconArrowRight } from "common/assets/icons";
import { PlanType } from "features/subscription/types";
import { ParentGate } from "features/parent/ParentGate";
import { actions, selectAllChatRoomMessageSync } from "features/user/slice";
import { AppUser, UserType, AccountType, ChatRoomMessageSync } from "features/user/types";
import { usePlan, useAccountLimits, useEnforcePlanLimit } from "features/subscription/hooks";

const MessageUnread = ({
  uid,
  chatRoomMessageSync,
}: {
  uid: string;
  chatRoomMessageSync: ChatRoomMessageSync[];
}) =>
  chatRoomMessageSync.filter(
    (sync) =>
      sync.uid === uid &&
      (dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
      ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null)),
  ).length ? (
    <div className="flex-col-el flex-center absolute right-0 top-0 bg-danger rounded-full w-[16px] h-[16px] z-50" />
  ) : null;

export const DashboardAccountSwitch = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const { handleFetchUserData } = useFetchUserData();
  const [open, setOpen] = useState(false);
  const [parentGateOpen, setParentGateOpen] = useState(false);
  const [selectUser, setSelectUser] = useState<AppUser | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const users = useSelector((state: RootState) => state.user.users);
  const active = useSelector((state: RootState) => state.user.active);
  const chatRoomMessageSync = useSelector(selectAllChatRoomMessageSync);
  const plan = usePlan();
  const accountLimits = useAccountLimits();
  const enforcePlanLimit = useEnforcePlanLimit();

  const otherUsers = users
    .filter(
      (u) =>
        u.uid !== active.uid &&
        u.type !== UserType.FROZEN &&
        u.account_type !== AccountType.PARENTS,
    )
    .sort((a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? 1 : -1));

  const hasOtherUsers = otherUsers.length > 0;
  const ITEMS_PER_PAGE = 4;
  const totalPages = Math.max(1, Math.ceil((otherUsers.length + 1) / ITEMS_PER_PAGE));
  const currentPageUsers = otherUsers.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );
  const showAddCardOnThisPage =
    currentPage === totalPages - 1 && currentPageUsers.length < ITEMS_PER_PAGE;

  const handleOpenModal = () => {
    play();
    setOpen(true);
    setSelectUser(null);
    setCurrentPage(0);
  };

  const triggerAddPlayer = () => {
    const kidsCount = users.filter((u) => u.account_type !== AccountType.PARENTS).length;
    const reachedLimit = kidsCount >= (accountLimits?.players?.limit ?? 1);
    if (plan === PlanType.TRIAL || reachedLimit) {
      handleDialog({ type: DialogType.USER_PROFILE, value: false });
      // TRIAL → PARENT_REGISTRATION_PROMPT、非 TRIAL → RestrictedFeatureDialog（CHILD_USER_LIMIT）
      // で先に「上限到達」の警告ダイアログを出してから、ユーザー操作でプラン画面に遷移させる
      enforcePlanLimit(RestrictedFeatureSource.CHILD_USER_LIMIT);
      return;
    }
    setParentGateOpen(true);
  };

  const handleOpenAddPlayer = () => {
    btnDelay(() => {
      play();
      triggerAddPlayer();
    });
  };

  const handleAddCardFromList = () => {
    btnDelay(() => {
      play();
      setOpen(false);
      setSelectUser(null);
      triggerAddPlayer();
    });
  };

  const handleParentGateClose = () => {
    setParentGateOpen(false);
  };

  const handleParentGateConfirm = () => {
    setParentGateOpen(false);
    handleDialog({ type: DialogType.USER_PROFILE, value: false });
    navigate("/parent?tab=player&action=create-player");
  };

  const handleCloseModal = () => {
    play();
    setOpen(false);
    setSelectUser(null);
  };

  const handleSelectUser = (user: AppUser) => () => {
    play();
    setSelectUser(user);
  };

  const handlePrevPage = () => {
    play();
    setCurrentPage((p) => Math.max(0, p - 1));
  };

  const handleNextPage = () => {
    play();
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
  };

  const handleConfirm = () => {
    if (!selectUser) return;

    btnDelay(async () => {
      setOpen(false);
      try {
        handleDialog({ type: DialogType.LOADING, value: true });
        handleFetchUserData({
          parent_uid: selectUser.parent_uid,
          uid: selectUser.uid,
        })
          .then(() => {
            dispatch(actions.setActiveUser(selectUser));
            dispatch(
              configActions.updateDialog({
                type: DialogType.USER_PROFILE,
                value: false,
              }),
            );
            handleDialog({ type: DialogType.LOADING, value: false });
            toast(
              <div>
                <p className="text text-textcolor-black">
                  {I18n.t("MSG_USER_PROFILE_CHANGE_USER_SUCCESS")}
                </p>
              </div>,
            );
            navigate("/top", { replace: true });
          })
          .catch((error) => {
            console.error(error);
            handleDialog({ type: DialogType.LOADING, value: false });
          });
      } catch (error) {
        console.error(error);
        handleDialog({ type: DialogType.LOADING, value: false });
      }
    });
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth={false}
        sx={{ zIndex: 20010 }}
        TransitionComponent={FadeTransition}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div className="flex-col-center" style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el flex-center relative border-[8px] border-white rounded-lg bg-white">
            <CloseBtn close={handleCloseModal} />

            <div className="flex-col-el w-[612px] bg-beige-60 rounded p-4">
              <p className="type-text type-body text-textcolor-black mb-3">
                {I18n.t("MSG_USER_PROFILE_CHANGE_USER")}
              </p>

              <div className="flex-row-el items-center gap-2 bg-white rounded-xl p-4">
                <div
                  className={`flex-row-el flex-center shrink-0 upsilon pointer-events-auto ${totalPages <= 1 ? "invisible" : ""}`}
                >
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 0}
                    className={`btn btn-primary w-[36px] h-[60px] ${currentPage === 0 ? "opacity-30 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex-col-center">
                      <IconArrowLeft />
                    </div>
                  </button>
                </div>

                <div className="flex-row-el items-start justify-start gap-3 flex-1">
                  {currentPageUsers.map((user) => (
                    <div
                      key={user.uid}
                      onClick={handleSelectUser(user)}
                      className="flex-col-center w-[100px] pointer-events-auto cursor-pointer"
                    >
                      <div
                        className={`flex-col-center bg-white rounded-full w-[80px] h-[80px] !box-content border-solid border-8 ${
                          user.uid === selectUser?.uid
                            ? "border-green-100/40"
                            : "border-transparent"
                        }`}
                        style={{
                          boxShadow:
                            "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                        }}
                      >
                        <MessageUnread uid={user.uid} chatRoomMessageSync={chatRoomMessageSync} />
                        <div className="flex-col-center w-full h-full rounded-full overflow-hidden">
                          <img
                            key={user.icon}
                            alt="プレイヤープロフィール画像"
                            src={user.icon}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                      <div className="flex-col-center w-[100px] mt-1">
                        <p className="text-body type-sm text-center text-gray2-100 text-body-ellipsis w-full">
                          {user.display_name}
                        </p>
                      </div>
                    </div>
                  ))}
                  {showAddCardOnThisPage && (
                    <div
                      key="__add_player_card__"
                      onClick={handleAddCardFromList}
                      className="flex-col-center w-[100px] pointer-events-auto cursor-pointer"
                    >
                      <div
                        className="flex-col-center bg-white rounded-full w-[80px] h-[80px] !box-content border-solid border-8 border-transparent"
                        style={{
                          boxShadow:
                            "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                        }}
                      >
                        <div className="flex-col-center w-full h-full rounded-full overflow-hidden bg-beige-60">
                          <span className="material-symbols-outlined type-title text-textcolor-black">
                            person_add
                          </span>
                        </div>
                      </div>
                      <div className="flex-col-center w-[100px] mt-1">
                        <p className="text-body type-sm text-center text-gray2-100 text-body-ellipsis w-full">
                          {I18n.t("MSG_DASHBOARD_ADD_PLAYER")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={`flex-row-el flex-center shrink-0 upsilon pointer-events-auto ${totalPages <= 1 ? "invisible" : ""}`}
                >
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages - 1}
                    className={`btn btn-primary w-[36px] h-[60px] ${currentPage === totalPages - 1 ? "opacity-30 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex-col-center">
                      <IconArrowRight />
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex-row-el flex-center alpha mt-3 gap-3">
                <button onClick={handleCloseModal} className="btn btn-blue">
                  <div className="flex-row-center gap-1 mb-1">
                    <span className="material-symbols-outlined !text-4xl">close</span>
                    <p className="text-body text-white text-font-size-body1">
                      {I18n.t("MSG_CLOSE_BTN")}
                    </p>
                  </div>
                </button>

                <button disabled={!selectUser} onClick={handleConfirm} className="btn btn-primary">
                  <div className="flex-row-center gap-1 mb-1">
                    <span className="material-symbols-outlined !text-4xl text-white">check</span>
                    <p className="text-body text-white text-font-size-body1">
                      {I18n.t("MSG_CONFIRM_BTN")}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <div className="flex-col-el items-center relative h-full">
        <button
          className="type-inherit-font opacity-button h-full"
          onClick={hasOtherUsers ? handleOpenModal : handleOpenAddPlayer}
        >
          <div
            className={`flex-col-center w-[140px] h-full px-2 rounded-xl border-2 border-solid border-beige-60 cursor-pointer ${open ? "bg-white/80" : "bg-white/60"}`}
            style={{ boxShadow: "0px 2px 0px #FFD1A1" }}
          >
            <div className="flex-col-center w-[48px] h-[48px]">
              <span className="material-symbols-outlined type-hero !leading-none text-textcolor-black">
                {hasOtherUsers ? "switch_account" : "person_add"}
              </span>
            </div>
            <p className="type-text type-sm text-textcolor-black leading-tight mt-1">
              {I18n.t(hasOtherUsers ? "MSG_DASHBOARD_SWITCH_PLAYER" : "MSG_DASHBOARD_ADD_PLAYER")}
            </p>
          </div>
        </button>
      </div>

      <ParentGate
        visible={parentGateOpen}
        confirm={handleParentGateConfirm}
        close={handleParentGateClose}
      />
    </>
  );
};
